import { ApolloError, useApolloClient, useMutation } from "@apollo/client";
import { Box, Flex, Link, Text } from "@chakra-ui/react";
import { CREATE_USER } from "api/mutations";
import { useLogin } from "api/hooks/useLogin";
import { readUser } from "api/user";
import { Button } from "components/button/Button";
import { Divider } from "components/divider/Divider";
import { InputGroup } from "components/input/InputGroup";
import { LogoMarquee } from "components/logoMarquee/LogoMarquee";
import { PasswordInputGroup } from "components/input/PasswordInputGroup";
import { Loader } from "components/progress/Loader";
import { Checkbox } from "components/selectors/Checkbox";
import type { FieldProps } from "formik";
import { Field, Form, Formik } from "formik";
import { capitalize } from "es-toolkit/compat";
import { observer } from "mobx-react";
import { type JSX, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useEffectOnce } from "react-use";
import { useStores } from "stores";
import { TRIAL_DAYS } from "stores/constants";
import { UTM_CAMPAIGNS } from "types";
import { LIVE_TEST_SIGNUP_PROD_GOOGLE_RECAPTCHA } from "utils/config";
import { ENV } from "utils/hostEnv";
import {
  DEFAULT_PLAN,
  IS_INVITED,
  NEW_USER_DATA,
  PERFORMANCE_PLAN_OVERRIDE,
  STORAGE_TYPE,
  storeDirectlyIntoStorage,
  UTM_PARAMS,
  WANTS_SUBSCRIPTION,
} from "utils/localStorage";
import * as Yup from "yup";
import { GoogleSSOButton } from "./GoogleSSOButton";
import { AUTH_CARD_WIDTH, LandingCopyComponent } from "./registerComponents";
import { ResendEmail } from "./ResendEmail";
import { AuthFormContainer } from "./AuthFormContainer";
import { RouterLink } from "components/routerLink/RouterLink";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { EXP_SIGNUP_INPUT_EMAIL_TITLE } from "utils/featuresExperiments";
import { EXP_EVENTS } from "utils/featuresGates";
import { statsig } from "utils/statsig";

const INTERNAL_DOMAINS = ["timescale.com", "tsdb.co", "tigerdata.com"]; //TODO: Parameterize this so additions/subtractions don't require codebase changes

const registrationValidationSchema = Yup.object().shape({
  fullName: Yup.string()
    .required("Required")
    .matches(/^\w+\s+\w+/, {
      message: "Please include first and last name",
      excludeEmptyString: true,
    }),
  email: Yup.string()
    .email("Please enter a valid email address")
    .test("no plus", 'Email cannot contain a "+" symbol', (value) => {
      if (!value) return true;
      const domain = value.split("@")[1];
      if (INTERNAL_DOMAINS.includes(domain)) return true;
      if (value.includes("+")) return false;
      return true;
    })
    .required("Required"),
  password: Yup.string()
    .min(12, "Please enter a valid password")
    .required("Required"),
});

const validateUtmCampaign = (
  param: string | undefined,
): UTM_CAMPAIGNS | null => {
  if (!param) return null;
  return Object.values(UTM_CAMPAIGNS).includes(param as UTM_CAMPAIGNS)
    ? (param as UTM_CAMPAIGNS)
    : null;
};

const USER_ALREADY_EXISTS = "a user with that email already exists";

type SignUpVariablesType = {
  email?: string;
  name?: string;
  subscription: boolean;
  password?: string;
  passwordConfirm?: string;
};

type SignUpEvent = "invited" | "signup" | UTM_CAMPAIGNS;

const signupCopy = (
  event: SignUpEvent,
): {
  header: JSX.Element | string;
  body: JSX.Element | string;
  tagline?: string;
} => {
  switch (true) {
    case event === "invited":
      return {
        header: "You’ve been invited to join Tiger Cloud",
        body: (
          <Text mt="1">
            Create your account to get started. No credit card required.
          </Text>
        ),
      };
    case event === UTM_CAMPAIGNS.VECTOR_LAUNCH:
      return {
        header: `Try pgai on Tiger Cloud free for ${TRIAL_DAYS.DEFAULT} days`,
        body: (
          <Text>
            Start building your AI applications with PostgreSQL.
            <br />
            No credit card required.
          </Text>
        ),
      };
    case event === UTM_CAMPAIGNS.TIME_SERIES:
      return {
        header: `Build powerful real-time data applications on Tiger Cloud for AWS for ${TRIAL_DAYS.DEFAULT} days`,
        body: <Text mt="1">No credit card required.</Text>,
      };
    case event === UTM_CAMPAIGNS.WELCOME_BACK:
      return {
        header: `We’re so glad to have you back. Here’s ${TRIAL_DAYS.WELCOME_BACK} days free on us!`,
        body: <Text mt="1">No credit card required.</Text>,
      };
    default:
      return {
        header: <>Postgres for time-series workloads at any scale.</>,
        tagline: "Real-world scale on a single Tiger Cloud service.",
        body: "Deploy on AWS or Microsoft Azure.",
      };
  }
};

export const SignUpPage = observer(function SignUpPage() {
  const { authStore, userStore } = useStores();
  const { login } = useLogin();
  const client = useApolloClient();
  const [confirmEmail, setConfirmEmail] = useState(false);
  const [checkEmail, setCheckEmail] = useState(false);
  const [formElement, setFormElement] = useState<HTMLFormElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [variables, setVariables] = useState<SignUpVariablesType>({
    email: undefined,
    name: undefined,
    subscription: true,
  });
  const [recaptcha, setRecaptcha] = useState(false);
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);

  const search = useSearch({
    from: "/publicLayout/signup",
  });

  const isTestEmail = /^timescale-ci(\+\d+)?@timescale\.com$/i.test(
    variables?.email || "",
  );

  const queryStringEmail = search.email;
  const queryStringInviteHash = search.hash;
  const utmCampaign = validateUtmCampaign(search.utm_campaign);
  const isMultiUserMember = !!queryStringInviteHash;

  const signUpEvent: SignUpEvent = queryStringInviteHash
    ? "invited"
    : utmCampaign || "signup";

  const { errors } = authStore;

  const [createUser] = useMutation(CREATE_USER);

  useEffectOnce(() => {
    const performancePlanOverride = search.default_plan === "performance";
    if (performancePlanOverride) {
      storeDirectlyIntoStorage(
        PERFORMANCE_PLAN_OVERRIDE,
        true,
        DEFAULT_PLAN,
        STORAGE_TYPE.LOCAL,
      );
    }
  });

  const onRecaptchaChange = async (token: string) => {
    setRecaptcha(false);

    try {
      setLoading(true);
      const { data, errors } = await createUser({
        variables: {
          recaptchaToken: token,
          ...variables,
          // The `createUser` mutation accepts extra vars for users
          // who joined via a multi-user invite. This allows auto email-validation,
          // to make the login process smoother for them.
          ...(isMultiUserMember && { inviteHash: queryStringInviteHash }),
        },
      });

      if (errors?.[0].message === USER_ALREADY_EXISTS) {
        setLoading(false);
        return setCheckEmail(true);
      }

      if (data) {
        // Save NEW_USER_DATA to localStorage
        // only after successful user creation.
        // Currently this is the flag we use to
        // determine that a user is new.
        const utmParams = {
          utm_campaign: search.utm_campaign || "",
          utm_content: search.utm_content || "",
          utm_keyword: search.utm_keyword || "",
          utm_medium: search.utm_medium || "",
          utm_source: search.utm_source || "",
          utm_term: search.utm_term || "",
        };

        storeDirectlyIntoStorage(
          IS_INVITED,
          isMultiUserMember,
          NEW_USER_DATA,
          STORAGE_TYPE.LOCAL,
        );

        storeDirectlyIntoStorage(
          UTM_PARAMS,
          utmParams,
          NEW_USER_DATA,
          STORAGE_TYPE.LOCAL,
        );

        storeDirectlyIntoStorage(
          WANTS_SUBSCRIPTION,
          variables.subscription,
          NEW_USER_DATA,
          STORAGE_TYPE.LOCAL,
        );

        // trigger GTM event on form submission
        if (formElement) {
          window.dataLayer.push({
            event: "gtm.formSubmit",
            "gtm.element": formElement, // The actual form DOM element
            "gtm.elementId": formElement.id,
          });
        }

        userStore.setThirdPartyIdentify(data.createUser);
        userStore.sendNewUserToHubspot(data.createUser);
        userStore.clearRegistrationForm();

        if (isMultiUserMember) {
          // Invited users get their email auto-validated by the backend
          // (via inviteHash), which skips the verify-email step that
          // normally establishes the session. Log them in directly so
          // they don't have to re-enter the password they just created.
          await login({
            email: variables.email,
            password: variables.password,
          });
          if (readUser(client)) {
            // login() already navigated to "/".
            return;
          }
          // Auto-login failed (network error, MFA challenge, etc.).
          // Fall back to manual login with the email pre-filled.
          navigate({
            to: `/login?email=${queryStringEmail}`,
          });
          return;
        }

        setConfirmEmail(true);
        setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      if (
        error instanceof ApolloError &&
        error.graphQLErrors.some((e) => e.message === USER_ALREADY_EXISTS)
      ) {
        setCheckEmail(true);
      }
    }
  };

  // Necessary to store the form element before it's removed from the DOM
  useEffect(() => {
    if (formRef.current) {
      setFormElement(formRef.current);
    }
  }, []);

  useEffectOnce(() => {
    authStore.resetEmailFlow();
    return () => authStore.resetEmailFlow();
  });

  if (loading) {
    return <Loader size="lg" overflow="hidden" />;
  }

  if (checkEmail) {
    return <ResendEmail />;
  }

  if (confirmEmail) {
    navigate({
      to: "/signup/success",
    });
    return null;
  }

  return (
    <Flex flexDir="column" w="100%" alignSelf="stretch">
      <Flex
        flex="1"
        w="100%"
        gap="36"
        flexWrap="wrap"
        justifyContent="center"
        alignItems="center"
      >
        <LandingCopyComponent {...signupCopy(signUpEvent)} />

        {/* See LoginPage: the card's width comes from the layout so it cannot
            grow when the Google SSO button paints in. */}
        <Box w={AUTH_CARD_WIDTH} maxW="100%">
          <SignUpComponent
            errors={errors}
            recaptcha={recaptcha}
            onRecaptchaChange={onRecaptchaChange}
            variables={variables}
            setVariables={setVariables}
            setRecaptcha={setRecaptcha}
            queryStringEmail={queryStringEmail}
            inviteHash={queryStringInviteHash}
            isTestEmail={isTestEmail}
            formRef={formRef}
          />
        </Box>
      </Flex>
      <Box hideBelow="xl" my="14" h="8">
        <LogoMarquee speed="100s" logoHeight="8" h="8" />
      </Box>
    </Flex>
  );
});

const SignUpComponent = ({
  errors,
  onRecaptchaChange,
  variables,
  setVariables,
  setRecaptcha,
  recaptcha,
  queryStringEmail,
  inviteHash,
  isTestEmail,
  formRef,
}: {
  errors: any;
  onRecaptchaChange: (token: any) => void;
  variables: SignUpVariablesType;
  setVariables: (variables: SignUpVariablesType) => void;
  setRecaptcha: (recaptcha: boolean) => void;
  recaptcha: boolean;
  queryStringEmail?: string | null;
  inviteHash?: string | null;
  isTestEmail?: boolean;
  formRef: React.RefObject<HTMLFormElement | null>;
}) => {
  const { userStore } = useStores();

  return (
    <Formik
      initialValues={{
        fullName: "",
        password: "",
        phone: "",
        privacy: true,
        subscription: true,
        email: queryStringEmail ?? "",
      }}
      validationSchema={registrationValidationSchema}
      onSubmit={({ fullName, email, password }, { setSubmitting }) => {
        setSubmitting(true);
        statsig.logEvent({
          eventName: EXP_EVENTS.CLICK_CLOUD_SIGN_UP,
          metadata: {
            email,
          },
        });
        setVariables({
          name: fullName,
          email: email,
          subscription: variables.subscription,
          password: password,
          passwordConfirm: password,
        });
        userStore.setRegistrationForm({
          name: fullName,
          email: email,
        });
        setRecaptcha(true);
        return;
      }}
    >
      {({ isSubmitting, values }) => (
        <Form
          ref={formRef as React.RefObject<HTMLFormElement>}
          id="signup-form"
        >
          <AuthFormContainer>
            <Text textStyle="h3" mb="12" textAlign="center">
              Sign up for Tiger Cloud
            </Text>

            <Flex flexDirection="column" gap="6">
              <Field name="fullName">
                {({ field, meta }: FieldProps) => (
                  <InputGroup
                    data-cy="fullName"
                    error={meta.touched && meta.error ? meta.error : null}
                    placeholder="Your name"
                    label="Full name"
                    autoFocus={true}
                    {...field}
                  />
                )}
              </Field>
              <Field name="email">
                {({ field, meta }: FieldProps) => (
                  <InputGroup
                    data-cy="email"
                    error={
                      (meta.touched && meta.error) || errors?.message
                        ? capitalize(errors?.message) || meta.error
                        : null
                    }
                    placeholder="Your email"
                    label={
                      statsig
                        .getExperiment(EXP_SIGNUP_INPUT_EMAIL_TITLE.ID)
                        .get(
                          EXP_SIGNUP_INPUT_EMAIL_TITLE.PARAMS.SHOW_FEATURE,
                          false,
                        )
                        ? "Email address"
                        : "Work email"
                    }
                    autoFocus={false}
                    {...field}
                  />
                )}
              </Field>
              <Field name="password">
                {({ field, meta }: FieldProps) => (
                  <PasswordInputGroup
                    data-cy="password"
                    label="Password"
                    autocomplete="on"
                    passwordType="create"
                    passwordStrength={Math.max(
                      0.01,
                      Math.min(
                        100,
                        (Math.log(values?.password?.length + 1) /
                          Math.log(13)) *
                          81,
                      ),
                    )}
                    placeholder="12 characters minimum"
                    validationRuleText="12 characters minimum"
                    error={meta.touched && (errors?.message || meta.error)}
                    {...field}
                  />
                )}
              </Field>
            </Flex>
            <Divider mt="5" mb="7" />
            <>
              {!recaptcha ? (
                <Flex flexDir="column" alignItems="center">
                  <Button
                    type="submit"
                    size="lg"
                    // To match GoogleSSOButton width
                    maxW="400px"
                    w="100%"
                    mb="2"
                    isLoading={isSubmitting}
                    data-cy="account-signup"
                  >
                    Sign up
                  </Button>

                  <GoogleSSOButton text="signup_with" inviteHash={inviteHash} />
                </Flex>
              ) : (
                <Flex>
                  <ReCAPTCHA
                    id="reCAPTCHA"
                    theme="dark"
                    sitekey={
                      isTestEmail
                        ? LIVE_TEST_SIGNUP_PROD_GOOGLE_RECAPTCHA
                        : import.meta.env.VITE_GOOGLE_TEST_RECAPTCHA ||
                          ENV.GOOGLE_RECAPTCHA
                    }
                    onChange={onRecaptchaChange}
                  />
                </Flex>
              )}

              <Checkbox
                mt="10"
                w="100%"
                justifyContent="center"
                color="fg.emphasized"
                isChecked={variables.subscription}
                onChange={(e) =>
                  setVariables({
                    ...variables,
                    subscription: e.target.checked,
                  })
                }
              >
                I'd like to receive Tiger Cloud updates
              </Checkbox>

              <Text mt="3" textAlign="center" color="fg.emphasized">
                Already have an account?
                <Link as={RouterLink} pl="1" to="/">
                  Log in
                </Link>
              </Text>

              <Text
                mt="3"
                textAlign="center"
                textStyle="caption"
                color="fg.emphasized"
              >
                By continuing, I agree to the Tiger Data
                <Link
                  data-cy="tos"
                  textStyle="captionLink"
                  p="0 4px"
                  href="https://www.tigerdata.com/legal/timescale-cloud-terms-of-service"
                  isExternal
                >
                  Terms of Service
                </Link>
                and acknowledge the
                <Link
                  data-cy="pp"
                  textStyle="captionLink"
                  pl="1"
                  href="https://www.tigerdata.com/legal/privacy"
                  isExternal
                >
                  Privacy Policy.
                </Link>
              </Text>
            </>
          </AuthFormContainer>
        </Form>
      )}
    </Formik>
  );
};
